import React, { createContext, useState, useEffect } from 'react';
import authService from '../services/authService';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if user is logged in on page refresh
    const checkUser = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          // Get user data (implicitly verifies token)
          const userData = await authService.getUserProfile();
          setCurrentUser(userData);
          
          // Get a CSRF token for future requests
          // I don't think we need this CSRF token here:
          // await authService.getCsrfToken();
        }
      } catch (err) {
        console.error('Error checking authentication state:', err);
        localStorage.removeItem('token'); // Clear invalid token
      } finally {
        setLoading(false);
      }
    };

    checkUser();
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      
      // Login and get token
      const response = await authService.login(email, password);
      localStorage.setItem('token', response.token);
      setCurrentUser(response.user);
      
      
      return response.user;
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
      throw err;
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      
      
      // Register and get token
      const response = await authService.register(userData);
      localStorage.setItem('token', response.token);
      setCurrentUser(response.user);
      
      return response.user;
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed');
      throw err;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setCurrentUser(null);
    authService.logout(); // Call backend logout endpoint
  };

  const updateProfile = async (userData) => {
    try {
      setError(null);
      const updatedUser = await authService.updateProfile(userData);
      setCurrentUser(updatedUser);
      return updatedUser;
    } catch (err) {
      setError(err.response?.data?.message || 'Profile update failed');
      throw err;
    }
  };

  const togglePublicProfile = async () => {
    try {
      setError(null);
      const response = await authService.togglePublicProfile();
      setCurrentUser({
        ...currentUser,
        isPublic: response.isPublic
      });
      return response;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile settings');
      throw err;
    }
  };
  
  const toggleNewsletterOptIn = async () => {
    try {
      setError(null);
      const response = await authService.toggleNewsletterOptIn();
      setCurrentUser({
        ...currentUser,
        newsletterOptIn: response.newsletterOptIn
      });
      return response;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update newsletter preferences');
      throw err;
    }
  };
  
  const toggleCollectionPrivacy = async () => {
    try {
      setError(null);
      const response = await authService.toggleCollectionPrivacy();
      setCurrentUser({
        ...currentUser,
        isCollectionPrivate: response.isCollectionPrivate
      });
      return response;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update collection privacy settings');
      throw err;
    }
  };
  
  const changePassword = async (currentPassword, newPassword) => {
    try {
      setError(null);
      const response = await authService.changePassword(currentPassword, newPassword);
      return response;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password');
      throw err;
    }
  };

  const value = {
    currentUser,
    loading,
    error,
    login,
    register,
    logout,
    updateProfile,
    toggleNewsletterOptIn,
    toggleCollectionPrivacy,
    changePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};